import { Stack, Typography, Box } from '@mui/material';
import { useDroppable } from '@dnd-kit/core';
import { useMemo, useState } from 'react';
import { useBoardContext } from '../../../../hooks/useBoardContext';
import { useFilterNotes } from '@/components/tour/notes/hooks/useFilterNotes';
import { useNotesContext } from '@/components/tour/notes/hooks/useNotesContext';
import { CountBadge } from '@/components/tour/notes/components/ui/CountBadge';
import { Button } from '@/components/ui/button';
import TextInput from '@/components/ui/text-input';
import { NoNotesFound } from '@/components/tour/notes/components/NoNotesFound';
import { DraggableCard } from '../../../draggable-card';
import { SidebarMode } from '../../../../config';
import { useSortNotes } from '@/components/tour/notes/hooks/useSortNotes';
import { NotesSortingType } from '@/components/tour/notes/enums';
import { Select } from '@/components/common/SelectField';
import { useAddNote } from '@/components/tour/notes/hooks/useAddNote';
import { MappedNoteCard } from '@/components/tour/notes/components/card';
import { useNotesStore } from '@/components/tour/notes/stores/notes.store';

export function Backlog() {
  const {
    setSidebarMode,
    boardFilters: { filters, getFiltersCount },
    applyFiltersToBacklog,
  } = useBoardContext();
  const { tags, setCurrentAction, setMediaUrls, setNoteToEdit, setIsBoardOpen } = useNotesContext();

  const notes = useNotesStore((state) => state.notes);
  const { selectNote } = useNotesStore((state) => state.actions);

  const [search, setSearch] = useState('');
  const { handleAddNote } = useAddNote();

  const [sortingType, setSortingType] = useState<NotesSortingType>(NotesSortingType.CreationDate);
  const [isSortingOpen, setIsSortingOpen] = useState<boolean>(false);

  const backlogNotes = useMemo(() => notes.filter((n) => n.backlogEnabled), [notes]);

  const { filteredCardNotes } = useFilterNotes({
    notes: backlogNotes,
    filters: applyFiltersToBacklog ? filters : undefined,
    searchTerm: search,
    tags,
  });

  const filtersCount = getFiltersCount();
  const isEmpty = !filteredCardNotes.length;

  const sortedNotes = useSortNotes(filteredCardNotes, sortingType);

  const { setNodeRef } = useDroppable({
    id: 'backlog',
  });

  return (
    <Stack
      sx={{
        overflow: 'auto',
        width: '100%',
        height: '100%',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap="10px"
        sx={{ padding: '10px 18px', borderBottom: '1px solid var(--Gray-300, #D0D5DD)' }}
      >
        <Typography
          variant="h6"
          sx={{
            overflow: 'hidden',
            color: 'var(--Black, #000)',
            textOverflow: 'ellipsis',
            fontFamily: 'Inter',
            fontSize: '18px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '28.873px',
          }}
        >
          Backlog
        </Typography>
        {filteredCardNotes.length > 0 && <CountBadge count={filteredCardNotes.length} />}
      </Stack>

      <Stack
        direction="row"
        gap="10px"
        sx={{ padding: '10px 18px', borderBottom: '1px solid var(--Gray-300, #D0D5DD)' }}
      >
        <TextInput
          value={search}
          containerProps={{ sx: { flexGrow: 1 } }}
          placeholder="Search..."
          startIcon={<i className="fa-regular fa-magnifying-glass" style={{ fontSize: '18px' }} />}
          onChange={(e) => setSearch(e.target.value)}
        />

        <Select
          value={sortingType}
          onChange={(v) => setSortingType(v.value)}
          options={[
            { value: NotesSortingType.CreationDate, label: 'Creation Date' },
            { value: NotesSortingType.DueDate, label: 'Due Date' },
          ]}
          onOpenChange={setIsSortingOpen}
          open={isSortingOpen}
          trigger={
            <Button
              onClick={() => setIsSortingOpen(!isSortingOpen)}
              sx={{
                width: '44px',
                height: '44px',
                minWidth: '44px',
                maxWidth: '44px',
                padding: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <i
                className="fa-regular fa-arrow-up-arrow-down"
                style={{ color: '#667085', fontSize: '20px' }}
              />
            </Button>
          }
        />
      </Stack>

      <Box
        ref={setNodeRef}
        sx={{
          flexGrow: '1',
          overflow: 'auto',
          background: '#F7F7F7',
          padding: '10px 18px',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          /* IE and Edge */
          msOverflowStyle: 'none',
          /* Firefox */
          scrollbarWidth: 'none',
        }}
      >
        {isEmpty ? (
          <Box sx={{ my: '50px' }}>
            {filtersCount > 0 ? (
              <NoNotesFound
                title="No results found"
                description="Try adjusting or clearing some filters to see more items."
                isButtonVisible={false}
              />
            ) : null}

            {filtersCount === 0 ? (
              <NoNotesFound
                title="No backlog items yet"
                description="Click “Add Note” to create your backlog note."
                buttonText="Add Note"
                onButtonClick={() => {
                  setIsBoardOpen(false);
                  handleAddNote();
                }}
              />
            ) : null}
          </Box>
        ) : (
          <Stack gap="10px">
            {sortedNotes.map((note) => {
              return (
                <DraggableCard key={note.id.toString()} id={note.id.toString()}>
                  <MappedNoteCard
                    note={note}
                    onClick={() => {
                      setSidebarMode(SidebarMode.Note);
                      selectNote(note.id);
                    }}
                    onDelete={() => {
                      setCurrentAction({
                        type: 'delete',
                        note,
                        onSuccess: () => setSidebarMode(SidebarMode.Backlog),
                      });
                    }}
                    onEdit={() => {
                      const message = note.messages.find((m) => m.isMainMessage);
                      if (!message) return;

                      setSidebarMode(SidebarMode.Note);
                      selectNote(note.id);
                      setMediaUrls(
                        message.mediaUrls.map((url) => ({ src: url, flaggedToDelete: false })),
                      );

                      setNoteToEdit({ note, message });
                    }}
                    onStatusChange={() => {
                      setCurrentAction({ type: 'change-status', note });
                    }}
                  />
                </DraggableCard>
              );
            })}
          </Stack>
        )}
      </Box>
    </Stack>
  );
}
